import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Btn, Select, Input, Empty } from "@/components/ui-kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, Upload, Calculator, Save, FileSpreadsheet, CheckCircle2, Lock, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const num = (v) => (v === "" || v == null ? "" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

export default function PayrollOps() {
  const [searchParams] = useSearchParams();

  const initialClient =
    searchParams.get("client_id") || "";

  const initialContract =
    searchParams.get("contract_id") || "";

  const initialMonth =
    searchParams.get("month") ||
    new Date().toISOString().slice(0, 7);

  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);

  const [sel, setSel] = useState({
    client_id: initialClient,
    contract_id: initialContract,
    month: initialMonth,
  });
  const [run, setRun] = useState(null);
  const [tab, setTab] = useState("attendance");

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);
  useEffect(() => {
    if (sel.client_id) api.get(`/clients/${sel.client_id}/contracts`).then((r) => setContracts(r.data));
    else setContracts([]);
  }, [sel.client_id]);

  const openRun = async () => {
    if (!sel.client_id || !sel.month) { toast.error("Select client and month"); return; }
    const { data } = await api.post("/payroll/runs", { client_id: sel.client_id, contract_id: sel.contract_id || null, month: sel.month });
    setRun(data); setTab("attendance");
  };
  const reloadRun = async () => { if (run) { const { data } = await api.get(`/payroll/runs/${run.id}`); setRun(data.run); } };

  return (
    <div>
      <PageHeader title="Attendance & Inputs" subtitle="Monthly payroll starts here — enter inputs, calculate the worksheet, reconcile" />
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div><label className="nk-overline block mb-1.5">Client</label>
            <Select value={sel.client_id} onChange={(e) => setSel({ ...sel, client_id: e.target.value })} data-testid="ops-client">
              <option value="">Select</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select></div>
          <div><label className="nk-overline block mb-1.5">Contract</label>
            <Select value={sel.contract_id} onChange={(e) => setSel({ ...sel, contract_id: e.target.value })} data-testid="ops-contract">
              <option value="">All / None</option>{contracts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select></div>
          <div><label className="nk-overline block mb-1.5">Month</label>
            <Input type="month" value={sel.month} onChange={(e) => setSel({ ...sel, month: e.target.value })} data-testid="ops-month" /></div>
          <Btn onClick={openRun} data-testid="open-run-btn">Open Payroll Run</Btn>
        </div>
        {run && <div className="mt-3 flex items-center gap-3 text-sm">
          <span className="text-slate-500">Run status:</span> <StatusBadge status={run.status} />
          <span className="text-slate-400">{run.month} · {run.employee_count || 0} employees</span>
        </div>}
      </Card>

      {run ? (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="attendance" data-testid="tab-attendance">1. Attendance</TabsTrigger>
            <TabsTrigger value="worksheet" data-testid="tab-worksheet">2. Payroll Worksheet</TabsTrigger>
            <TabsTrigger value="calibration" data-testid="tab-calibration">Calibration & Reconcile</TabsTrigger>
          </TabsList>
          <TabsContent value="attendance" className="mt-4"><Attendance run={run} onCalc={() => { reloadRun(); setTab("worksheet"); }} /></TabsContent>
          <TabsContent value="worksheet" className="mt-4"><Worksheet run={run} onChange={reloadRun} /></TabsContent>
          <TabsContent value="calibration" className="mt-4"><Calibration run={run} /></TabsContent>
        </Tabs>
      ) : <Empty title="Select a client and month" hint="Open a payroll run to enter monthly inputs." />}
    </div>
  );
}

function Attendance({ run, onCalc }) {
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [locked, setLocked] = useState(false);
  const [servicePct, setServicePct] = useState(
    run.service_charge_pct != null
      ? String(run.service_charge_pct)
      : ""
  );

  useEffect(() => {
    setServicePct(
      run.service_charge_pct != null
        ? String(run.service_charge_pct)
        : ""
    );
  }, [
    run.id,
    run.service_charge_pct
  ]);

  const load = () => api.get(`/payroll/runs/${run.id}/attendance`).then((r) => {
    setCols(r.data.input_columns); setRows(r.data.rows); setLocked(r.data.locked);
  });
  useEffect(() => { load(); }, [run.id]);

  const setVal = (uan, code, v) => setRows((rs) => rs.map((r) => r.uan === uan ? { ...r, inputs: { ...r.inputs, [code]: v } } : r));
  const save = async () => {
    try { await api.put(`/payroll/runs/${run.id}/attendance`, { rows: rows.map((r) => ({ uan: r.uan, inputs: r.inputs })) }); toast.success("Attendance saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const calc = async () => {
    if (String(servicePct).trim() === "") {
      toast.error("Enter Service Charge %");
      return;
    }

    const pct = Number(servicePct);

    if (
      !Number.isFinite(pct) ||
      pct < 0
    ) {
      toast.error(
        "Enter a valid Service Charge %"
      );
      return;
    }

    try {
      await save();

      await api.post(
        `/payroll/runs/${run.id}/worksheet`,
        {
          service_charge_pct: pct,
        }
      );

      toast.success("Payroll calculated");
      onCalc();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/payroll/runs/${run.id}/attendance-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Uploaded ${data.valid} rows (${data.errors.length} errors)`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const shown = rows.filter((r) => !q || (r.uan || "").includes(q) || (r.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 justify-between">
        <input placeholder="Search UAN / name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="attendance-search"
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <div className="flex gap-2">
          <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${run.id}/attendance-template`, `attendance_${run.month}.xlsx`)} data-testid="dl-template"><Download size={15} /> Template</Btn>
          <label className={`inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold cursor-pointer bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 ${locked ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-attendance">
            <Upload size={15} /> Upload<input type="file" accept=".xlsx,.csv" className="hidden" disabled={locked} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          </label>
          <Btn
            variant="outline"
            onClick={save}
            disabled={locked}
            data-testid="save-attendance"
          >
            <Save size={15} /> Save
          </Btn>

          <div className="w-32">
            <label className="nk-overline block mb-1">
              Service Charge %
            </label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={servicePct}
              onChange={(e) =>
                setServicePct(e.target.value)
              }
              disabled={locked}
              placeholder=""
              data-testid="ops-service-charge-pct"
            />
          </div>

          <Btn
            onClick={calc}
            disabled={locked}
            data-testid="calc-worksheet"
          >
            <Calculator size={16} />
            Calculate Payroll
          </Btn>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[540px] overflow-y-auto">
        {rows.length ? (
          <table className="nk-table" data-testid="attendance-table">
            <thead><tr>
              <th className="nk-sticky-col">UAN</th><th>Employee</th><th>Designation</th>
              {cols.map((c) => <th key={c.code}>{c.name}</th>)}
            </tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.uan}>
                  <td className="nk-sticky-col nk-num font-semibold">{r.uan}</td><td>{r.name}</td><td>{r.designation}</td>
                  {cols.map((c) => (
                    <td key={c.code}>
                      <input value={r.inputs[c.code] ?? ""} disabled={locked} onChange={(e) => setVal(r.uan, c.code, e.target.value)}
                        data-testid={`input-${r.uan}-${c.code}`}
                        className="w-20 border border-slate-200 rounded px-1.5 py-1 text-sm nk-num focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : <Empty title="No employees deployed" hint="Deploy employees to this client/contract first." />}
      </div>
    </Card>
  );
}

function Worksheet({ run, onChange }) {
  const [ws, setWs] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get(`/payroll/runs/${run.id}/worksheet`).then((r) => setWs(r.data));
  useEffect(() => { load(); }, [run.id]);
  const setStatus = async (status) => {
    try {
      await api.post(`/payroll/runs/${run.id}/status`, { status });
      toast.success(`Payroll ${status}`);
      onChange();
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const reopenPayroll = async () => {
    const ok = window.confirm(
      `Reopen this ${ws.status} payroll for editing?`
    );

    if (!ok) return;

    const reason = window.prompt(
      "Reason for reopening payroll:"
    );

    if (reason === null) return;

    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }

    try {
      await api.post(
        `/payroll/runs/${run.id}/status`,
        {
          status: "calculated",
          reason: reason.trim(),
        }
      );

      toast.success("Payroll reopened for editing");
      onChange();
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const genInvoice = async () => {
    try { const { data } = await api.post(`/payroll/runs/${run.id}/invoice`); toast.success(`Invoice ${data.number} generated`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!ws) return <Empty title="Loading" />;
  if (!ws.rows.length) return <Empty title="Not calculated yet" hint="Enter attendance and click Calculate Payroll." />;
  const shown = ws.rows.filter((r) => !q || (r.uan || "").includes(q) || (r.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <div className="nk-head font-semibold text-sm">Payroll Worksheet</div><StatusBadge status={ws.status} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ws-search"
            className="border border-slate-200 rounded-md px-3 py-1.5 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${run.id}/worksheet-export?format=xlsx`, `worksheet_${run.month}.xlsx`)} data-testid="ws-xlsx"><FileSpreadsheet size={15} /> Excel</Btn>
          <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${run.id}/worksheet-export?format=csv`, `worksheet_${run.month}.csv`)} data-testid="ws-csv">CSV</Btn>
          {ws.status === "calculated" && (
            <Btn
              onClick={() => setStatus("finalized")}
              data-testid="ws-finalize"
            >
              <CheckCircle2 size={15} /> Finalize
            </Btn>
          )}

          {ws.status === "finalized" && (
            <Btn
              onClick={() => setStatus("locked")}
              data-testid="ws-lock"
            >
              <Lock size={15} /> Lock
            </Btn>
          )}

          {["finalized", "locked"].includes(ws.status) && (
            <Btn
              variant="outline"
              onClick={reopenPayroll}
              data-testid="ws-reopen"
            >
              <RotateCcw size={15} /> Reopen for Editing
            </Btn>
          )}

          <Btn
            variant="outline"
            onClick={genInvoice}
            data-testid="ws-invoice"
          >
            Generate Invoice
          </Btn>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="nk-table" data-testid="worksheet-table">
          <thead><tr>
            <th className="nk-sticky-col">UAN</th><th>Employee</th><th>Designation</th>
            {ws.columns.map((c) => <th key={c.code} className={c.editable ? "text-blue-700" : ""}>{c.name}</th>)}
          </tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>
                <td className="nk-sticky-col nk-num font-semibold">{r.uan}</td><td>{r.name}</td><td>{r.designation}</td>
                {ws.columns.map((c) => <td key={c.code} className="nk-num">{num(r.values[c.code])}</td>)}
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="font-bold bg-slate-100">
            <td className="nk-sticky-col" style={{ background: "#f1f5f9" }}>TOTAL</td><td></td><td></td>
            {ws.columns.map((c) => <td key={c.code} className="nk-num">{num(ws.totals[c.code])}</td>)}
          </tr></tfoot>
        </table>
      </div>
    </Card>
  );
}

function Calibration({ run }) {
  const [recon, setRecon] = useState(null);
  const load = () => api.get(`/payroll/runs/${run.id}/reconcile`).then((r) => setRecon(r.data));
  useEffect(() => { load(); }, [run.id]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/payroll/runs/${run.id}/calibration`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Reference stored: ${data.stored} rows`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const s = recon?.summary;

  return (
    <div>
      <Card className="p-4 mb-4 flex items-center gap-3">
        <label className="inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold cursor-pointer text-white" style={{ background: "var(--nk-navy)" }} data-testid="upload-calibration">
          <Upload size={15} /> Upload Reference Excel<input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
        <p className="text-sm text-slate-500">Upload Nakshatra's approved workbook (columns like UAN Number, Earned Wage, Net Pay, PF, ESI, Client Cost). Reference values are stored separately and compared to system output.</p>
      </Card>

      {s && (
        <Card className="p-4 mb-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <span>Employees matched: <b className="text-emerald-600">{s.matched}/{s.total}</b></span>
            <span>Mismatches: <b className="text-red-600">{s.mismatched}</b></span>
            <span>Missing: <b className="text-amber-600">{s.missing}</b></span>
            <span>Reference loaded: <b>{s.has_reference ? "Yes" : "No"}</b></span>
          </div>
        </Card>
      )}

      {recon?.rows?.length ? (
        <div className="space-y-3">
          {recon.rows.filter((r) => r.status === "MISMATCH" || r.fields.length).slice(0, 100).map((r) => (
            <Card key={r.uan} className="overflow-hidden" data-testid={`recon-${r.uan}`}>
              <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-3">
                <span className="nk-num font-semibold">{r.uan}</span><span className="text-slate-600">{r.name}</span>
                <StatusBadge status={r.status === "MATCH" ? "finalized" : r.status === "MISMATCH" ? "overdue" : "draft"} />
              </div>
              {r.fields.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="nk-table">
                    <thead><tr><th>Field</th><th>Excel Expected</th><th>System Calculated</th><th>Difference</th><th>Status</th></tr></thead>
                    <tbody>
                      {r.fields.map((f) => (
                        <tr key={f.field} className={f.status === "MISMATCH" ? "bg-red-50" : ""}>
                          <td className="capitalize">{f.field.replace(/_/g, " ")}</td>
                          <td className="nk-num">{num(f.expected)}</td><td className="nk-num">{num(f.calculated)}</td>
                          <td className="nk-num">{num(f.difference)}</td>
                          <td><StatusBadge status={f.status === "MATCH" ? "finalized" : "overdue"} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : <Empty title="No reconciliation yet" hint="Upload a reference workbook after calculating the worksheet." />}
    </div>
  );
}
